import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  usoCondicaoPagamentoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Condição de pagamento (`.spec/010`, Cadastros › Financeiro; `.spec/016`,
 * seção 6).
 *
 * É o cadastro que diz **em quantas vezes e a cada quantos dias** um título se
 * divide: "à vista", "30/60/90", "entrada + 5×30". Não guarda valor nem data —
 * quem tem isso é o documento que a escolhe; aqui ficam só os prazos, contados
 * em dias a partir da data do documento.
 *
 * O cadastro é **mestre e detalhe**, e os dois viajam juntos num payload só. A
 * alternativa — uma rota para as parcelas — deixaria existir condição gravada
 * sem parcela nenhuma no intervalo entre as duas chamadas, e é justamente essa
 * a linha que o gerador de títulos não sabe usar.
 */

/* -------------------------------------------------------------------------- */
/* Uso                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Pagamento, recebimento ou ambos.
 *
 * O `z.enum` mora em `comum.ts`, junto com os outros códigos estáveis do
 * domínio, e aqui só se derivam a lista e o tipo. Redeclará-lo daria dois
 * lugares para acrescentar um código novo — e o esquecido seria justamente o
 * que a coluna `CHECK condicoes_pagamento_uso_valido` valida.
 */
export const USOS_CONDICAO_PAGAMENTO = usoCondicaoPagamentoSchema.options;
export type UsoCondicaoPagamento = z.infer<typeof usoCondicaoPagamentoSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_USO_CONDICAO_PAGAMENTO: Readonly<Record<UsoCondicaoPagamento, string>> = {
  PAGAMENTO: 'Pagamento',
  RECEBIMENTO: 'Recebimento',
  AMBOS: 'Ambos',
};

/**
 * O que cada uso implica, para a dica do formulário.
 *
 * "Pagamento" e "recebimento" são ambíguos sozinhos — dependem de quem fala. A
 * frase diz de que lado a construtora está, que é a única coisa que o usuário
 * precisa decidir no campo.
 */
export const DICA_USO_CONDICAO_PAGAMENTO: Readonly<Record<UsoCondicaoPagamento, string>> = {
  PAGAMENTO: 'Só no que a construtora paga — compra, contrato de empreitada, despesa.',
  RECEBIMENTO: 'Só no que a construtora recebe — medição, venda de unidade.',
  AMBOS: 'Oferecida nos dois lados. É o caso de prazos genéricos como 30/60/90.',
};

/* -------------------------------------------------------------------------- */
/* Parcelas                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Teto de parcelas.
 *
 * Financiamento de unidade chega a 120 prestações, e é o maior caso real que
 * este cadastro precisa descrever. O número existe para que um `curl` com dez
 * mil linhas não vire dez mil `INSERT` numa transação — não para julgar o
 * negócio do usuário.
 */
export const MAXIMO_PARCELAS_CONDICAO = 120;

/**
 * Teto de dias de uma parcela: dez anos.
 *
 * Prazo maior que isso é erro de digitação — 3600 no lugar de 360 —, e o
 * sintoma sem o teto seria um título vencendo em 2036 sem ninguém notar.
 */
export const MAXIMO_DIAS_PARCELA = 3650;

/**
 * Uma parcela: o número dela e quantos dias depois do documento ela vence.
 *
 * `qtdDias` aceita **zero**, que é a entrada à vista — e é por isso que o
 * mínimo não é 1. Uma condição "entrada + 30 + 60" tem três parcelas, e a
 * primeira vence no dia da compra.
 *
 * Os dois nomes são os mesmos de `ParcelaCondicao`, em `@construtora/domain`:
 * é este objeto que `gerarParcelas` recebe para calcular vencimento e valor de
 * cada título. Nomes diferentes exigiriam uma tradução no meio do caminho, e é
 * nela que a parcela 3 chegaria com os dias da 2.
 */
export const parcelaCondicaoPagamentoSchema = z.object({
  parcela: z
    .number()
    .int('O número da parcela é inteiro.')
    .min(1, 'A numeração das parcelas começa em 1.')
    .max(MAXIMO_PARCELAS_CONDICAO),
  qtdDias: z
    .number()
    .int('O prazo é em dias inteiros.')
    .min(0, 'O prazo não pode ser negativo. Zero é a parcela à vista.')
    .max(MAXIMO_DIAS_PARCELA, `O prazo máximo é de ${MAXIMO_DIAS_PARCELA} dias.`),
});

export type ParcelaCondicaoPagamento = z.infer<typeof parcelaCondicaoPagamentoSchema>;

/**
 * Numeração sequencial a partir de 1, e prazo que nunca anda para trás.
 *
 * A sequência não é capricho: a parcela é a **posição** na condição, e um
 * conjunto `1, 2, 4` faria o gerador de títulos emitir três documentos numerados
 * como quatro. O `UNIQUE (tenant_id, condicao_pagamento_id, parcela)` recusa a
 * repetição no banco, mas nada lá impede o buraco.
 *
 * O prazo é **estritamente crescente**: cada parcela vence depois da anterior.
 *
 * A regra já foi mais frouxa — aceitava o empate, com o argumento de que uma
 * condição com duas entradas de formas de pagamento diferentes existe e recusá-la
 * seria opinar sobre o negócio do usuário. O empate saiu porque a parcela aqui
 * não carrega forma de pagamento nenhuma: duas linhas com o mesmo prazo geram
 * dois títulos com o mesmo vencimento e o mesmo valor, indistinguíveis no contas
 * a pagar. Quem quer isso lança uma parcela só, pelo valor somado — e quem
 * digitou o mesmo número duas vezes quase sempre errou o segundo.
 */
function validarSequencia(parcelas: ParcelaCondicaoPagamento[], ctx: z.RefinementCtx): void {
  parcelas.forEach((atual, indice) => {
    if (atual.parcela !== indice + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [indice, 'parcela'],
        message: `As parcelas são numeradas em sequência a partir de 1. Esta deveria ser a ${indice + 1}ª.`,
      });
      return;
    }

    const anterior = parcelas[indice - 1];
    if (anterior && atual.qtdDias <= anterior.qtdDias) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [indice, 'qtdDias'],
        message: `O prazo precisa ser maior que o da parcela anterior (${anterior.qtdDias}).`,
      });
    }
  });
}

const parcelasSchema = z
  .array(parcelaCondicaoPagamentoSchema)
  .min(1, 'A condição precisa de pelo menos uma parcela.')
  .max(MAXIMO_PARCELAS_CONDICAO, `A condição tem no máximo ${MAXIMO_PARCELAS_CONDICAO} parcelas.`)
  .superRefine(validarSequencia);

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * **Não tem `qtdParcelas`**, e a coluna existe. Ela é sempre o tamanho da lista,
 * e aceitá-la aqui abriria a única forma de os dois discordarem: uma condição
 * dizendo "3 parcelas" com quatro linhas gravadas. Quem preenche a coluna é o
 * service, contando.
 */
export const criarCondicaoPagamentoSchema = z.object({
  descricao: z
    .string()
    .trim()
    .min(2, 'Informe a descrição da condição.')
    .max(200, 'A descrição tem no máximo 200 caracteres.'),
  uso: usoCondicaoPagamentoSchema,
  parcelas: parcelasSchema,
  ativo: z.boolean().prefault(true),
});

export type CriarCondicaoPagamento = z.infer<typeof criarCondicaoPagamentoSchema>;
export const atualizarCondicaoPagamentoSchema = criarCondicaoPagamentoSchema;
export type AtualizarCondicaoPagamento = z.infer<typeof atualizarCondicaoPagamentoSchema>;

export const condicaoPagamentoSchema = z.object({
  id: uuidSchema,
  descricao: z.string(),
  uso: usoCondicaoPagamentoSchema,
  /** Sempre igual a `parcelas.length`. Gravado porque a listagem ordena por ele. */
  qtdParcelas: z.number().int(),
  /**
   * As parcelas vêm **na listagem também**, não só no cadastro aberto.
   *
   * São os prazos que distinguem duas condições com nomes parecidos, e sem eles
   * a coluna "Parcelas" mostraria "3" para "30/60/90" e para "60/120/180". O
   * custo é uma segunda consulta por página, não uma por linha.
   */
  parcelas: z.array(parcelaCondicaoPagamentoSchema),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type CondicaoPagamento = z.infer<typeof condicaoPagamentoSchema>;

export const listaCondicoesPagamentoSchema = respostaListaSchema(condicaoPagamentoSchema);
export type ListaCondicoesPagamento = z.infer<typeof listaCondicoesPagamentoSchema>;

/**
 * Listagem, com um filtro além dos comuns.
 *
 * `uso` porque quem lança uma compra não quer ver condição de recebimento na
 * lista — e o campo de busca não resolve: "30/60/90" não diz de que lado ela
 * serve. O filtro traz também as de uso `AMBOS`, que valem nos dois lados; quem
 * decide isso é a API, para a tela não ter de conhecer a regra.
 */
export const listarCondicoesPagamentoQuerySchema = listagemDeCadastroSchema.extend({
  uso: usoCondicaoPagamentoSchema.optional(),
});

export type ListarCondicoesPagamentoQuery = z.infer<typeof listarCondicoesPagamentoQuerySchema>;

/**
 * Colunas de ordenação da listagem.
 *
 * A ordem padrão é `descricao`, crescente: o cadastro é curto e se procura pelo
 * nome. `qtdParcelas` entra porque "quais condições são à vista?" é a segunda
 * pergunta que se faz a esta tela.
 */
export const COLUNAS_ORDENAVEIS_CONDICAO_PAGAMENTO = [
  'descricao',
  'qtdParcelas',
  'createdAt',
] as const;

/**
 * Resumo dos prazos, para a tela: `30/60/90`.
 *
 * Vive no contrato, e não no componente, porque a listagem e o seletor de
 * condição do documento mostram a mesma coisa — duas implementações escreveriam
 * "0" e "à vista" para o mesmo cadastro.
 */
export function resumoDePrazos(parcelas: readonly ParcelaCondicaoPagamento[]): string {
  if (parcelas.length === 0) return '—';
  if (parcelas.length === 1 && parcelas[0]?.qtdDias === 0) return 'À vista';

  return parcelas.map((p) => p.qtdDias).join('/');
}
